import React from 'react';
import { debounce } from 'lodash';
import {
  getNameForUserUuid,
  findOrCreateStreamForStudentUuid,
} from 'coaching/actions/CoachingActions';
import { assertions } from 'lib/assertions';
import BoxBody from 'components/BoxBody';
import Button from 'components/Button';
import Dialog from 'components/Dialog';
import FormElement from 'components/FormElement';
import Heading from 'components/Heading';
import Input from 'components/Input';
import LoadingIndicator from 'components/LoadingIndicator';
import Row from 'components/Row';
import Text from 'components/Text';

export type Props = {
  onRequestHide: () => void;
};

type State = {
  studentUuid?: string;
  studentName?: string;
  loadingStudentName: boolean;
  submitting: boolean;
};

export default class CoachingAddCoachingStreamDialog extends React.Component<Props, State> {
  state: State = {
    submitting: false,
    loadingStudentName: false,
  };

  handleAddStream = () => {
    const { studentUuid } = this.state;
    if (!studentUuid) {
      console.error(
        'CoachingAddCoachingStreamDialog - tried to set add stream without studentUuid'
      );
      return;
    }

    this.setState({ submitting: true });
    return findOrCreateStreamForStudentUuid(studentUuid).then(this.props.onRequestHide, () =>
      this.setState({ submitting: false })
    );
  };

  setStudentUuidAndName = (studentUuid: string) => {
    this.setState({ studentUuid }, this.fetchAndSetStudentName);
  };

  fetchAndSetStudentName = debounce(() => {
    const { studentUuid } = this.state;
    if (!studentUuid) {
      console.error(
        'CoachingAddCoachingStreamDialog - tried to set student name without studentUuid'
      );
      return;
    }

    this.setState({ loadingStudentName: true });
    return getNameForUserUuid(studentUuid).then(name =>
      this.setState({ studentName: name, loadingStudentName: false })
    );
  }, 500);

  render() {
    const { loadingStudentName, studentName, studentUuid, submitting } = this.state;
    const inputSubtext = (loadingStudentName || studentName) && (
      <Text size="xxsmall" color="thunder" data-test="student-name-text">
        {loadingStudentName ? <LoadingIndicator size="xsmall" /> : `Name: ${studentName}`}
      </Text>
    );

    return (
      <Dialog onRequestHide={this.props.onRequestHide}>
        <BoxBody>
          <Row size="small">
            <Heading level="2" serif>
              Create a new coaching chat
            </Heading>
            <Text color="thunder">
              If a coaching chat already exists with the student, we will not return an error, but{' '}
              <Text weight="semibold">we will not create a new chat</Text>.
            </Text>
          </Row>
          <Row>
            <FormElement
              data-test="student-uuid-input"
              element={Input}
              validate={e => assertions.uuid(e.target.value)}
              label=""
              onValidChange={e => this.setStudentUuidAndName(e.target.value)}
              subtext={inputSubtext}
              elementProps={{ value: studentUuid, placeholder: "Paste the student's user UUID" }}
            />
          </Row>
          <Button
            data-test="add-new-stream-button"
            loading={submitting}
            disabled={!studentName && !studentUuid}
            onClick={this.handleAddStream}>
            Create chat
          </Button>
        </BoxBody>
      </Dialog>
    );
  }
}
